import React from "react";
import { Container, Section, PageHero, Card, Eyebrow, CTABand } from "@/components/ui-bits/Primitives";
import LeadForm from "@/components/forms/LeadForm";
import { GRANT_PROGRAMS, IMAGES } from "@/lib/site-data";
import { CheckCircle2, FileText, Calculator, Cable, ClipboardCheck, ShieldCheck } from "lucide-react";

const CAPABILITIES = [
    { icon: FileText, title: "Grant Application Assistance", body: "Narrative drafting, eligibility, supporting documentation, and submission management." },
    { icon: Calculator, title: "Budget Development", body: "Cost estimates, match strategy, cost-share modeling, and ROI projections." },
    { icon: Cable, title: "Utility Coordination", body: "Make-ready, transformer upgrades, service planning, and interconnection." },
    { icon: ClipboardCheck, title: "Reporting & Compliance", body: "Quarterly reporting, performance metrics, audit-ready documentation." },
    { icon: ShieldCheck, title: "American Vendor Compliance", body: "Equipment sourcing from American manufacturers, supplier documentation, and procurement program eligibility." },
];

const FIELDS = [
    { name: "name", label: "Full Name", required: true },
    { name: "company", label: "Organization" },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "phone", label: "Phone" },
    { name: "grant_program", label: "Grant Program of Interest", as: "select", options: ["Texas NEVI", "TCEQ AFFP", "NCTCOG", "Federal CFI", "USDA Rural", "Other / Unsure"] },
    { name: "project_location", label: "Project Location", placeholder: "City, County, State" },
    { name: "project_scope", label: "Project Scope", as: "textarea", colSpan: 2, placeholder: "Number of ports, kW, site context, fleet/public, partners…" },
    { name: "message", label: "Additional Notes", as: "textarea", colSpan: 2 },
];

export default function Grants() {
    return (
        <div data-testid="grants-page">
            <PageHero
                kicker="Grant Assistance"
                title="Funding strategy is our specialty."
                lead="From Texas NEVI to TCEQ AFFP, NCTCOG, and the full federal infrastructure program portfolio — we navigate the complexity so you win the awards."
                image={IMAGES.blueprint}
            />

            <Section>
                <Container>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {GRANT_PROGRAMS.map((g) => {
                            const I = g.icon;
                            return (
                                <Card key={g.name}>
                                    <I className="w-7 h-7 text-electric-400 mb-5" strokeWidth={1.5} />
                                    <h3 className="font-heading text-lg text-white">{g.name}</h3>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{g.blurb}</p>
                                </Card>
                            );
                        })}
                    </div>
                </Container>
            </Section>

            <Section className="border-t border-white/5">
                <Container>
                    <Eyebrow className="mb-4">Capabilities</Eyebrow>
                    <h2 className="font-heading font-medium text-white text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-3xl">From narrative to compliance — full-cycle grant execution.</h2>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {CAPABILITIES.map((c) => {
                            const I = c.icon;
                            return (
                                <Card key={c.title}>
                                    <I className="w-7 h-7 text-electric-400 mb-5" strokeWidth={1.5} />
                                    <h3 className="font-heading text-lg text-white">{c.title}</h3>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.body}</p>
                                </Card>
                            );
                        })}
                    </div>
                </Container>
            </Section>

            <Section className="border-t border-white/5">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <Eyebrow className="mb-4">Apply for Grant Assistance</Eyebrow>
                            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight leading-tight">Tell us about your grant program.</h2>
                            <p className="mt-4 text-slate-400 leading-relaxed">Submit the details below — our programs team will respond within one business day with next-step recommendations.</p>
                        </div>
                        <div className="lg:col-span-7">
                            <Card hover={false} className="!p-8 md:!p-10">
                                <LeadForm type="grant" fields={FIELDS} submitLabel="Request Grant Assistance" testIdPrefix="grant" />
                            </Card>
                        </div>
                    </div>
                </Container>
            </Section>

            <CTABand title="Have a deadline approaching?" subtitle="We've supported NEVI, TCEQ, NCTCOG, and federal grant cycles. We can move fast." />
        </div>
    );
}
